"use client";

import { useEffect, useState } from "react";
import {
  internshipMapFilters,
  residencyMapFilters,
} from "@/components/doctor-training/dashboard/model";
import { FacilityTypeFilter } from "@/components/doctor-training/facility-type-filter";
import { LazyList } from "@/components/medical-facility/lazy-list";
import { useImmer } from "use-immer";
import { Filters as ResidencyFilters } from "@/components/residency-map/filters/filters";
import { Filters as InternshipFilters } from "@/components/internship-map/filters/filters";
import { doctorTrainingDashboardSorts } from "@/components/doctor-training/dashboard/sorts";
import { Sort } from "@/components/common/sort/sort";

import dynamic from "next/dynamic";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createPath } from "@/service/routing/routing";
import { useRouter } from "@bprogress/next";
import { cn } from "@/lib/utils";
import { SectionTitleWithIcon } from "@/components/common/section-title-with-icon";

const InteractiveMap = dynamic(
  () => import("@/components/doctor-training/interactive-map/interactive-map"),
  {
    ssr: false,
  },
);

export const Dashboard = ({
  setupType,
  favouriteOnly = false,
  facilities,
  filterSource,
  medicalChambers = [],
}: {
  setupType: DoctorTrainingType;
  favouriteOnly?: boolean;
  facilities: MedicalFacilityCard[];
  filterSource: ResidencyMapFilterSource | InternshipMapFilterSource | false;
  medicalChambers?: MedicalChamber[];
}) => {
  const [resetState, resetMap] = useState(false);
  const [geoFilter, setGeoFilter] = useImmer<{
    topLeft: Coordinate;
    bottomRight: Coordinate;
  } | null>(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const setups = {
    residency: {
      favouritePath: "residency_map.favourite",
      title: "Mapa rezydentur",
      desc: "W tym miejscu znajdziesz mapę wszystkich jednostek szkoleniowych wraz z opiniami na ich temat. Sprawdź wykaz wolnych miejsc na tryb rezydencki i pozarezydencki oraz całkowitą liczbę miejsc szkoleniowych zatwierdzoną przez CMKP.",
      filters: (
        <ResidencyFilters
          filtersSource={filterSource as ResidencyMapFilterSource}
        />
      ),
      facilityList: (
        <LazyList
          resetMap={resetMap}
          facilities={facilities}
          geoFilter={geoFilter}
          setupType={setupType}
          inFavourite={!filterSource}
          filterSource={filterSource}
        />
      ),
      facilityTypeFilter: <FacilityTypeFilter filters={residencyMapFilters} />,
      map: isClient ? (
        <InteractiveMap
          facilities={facilities}
          setGeoFilter={setGeoFilter}
          type={"residency"}
          resetState={resetState}
          resetMap={resetMap}
        />
      ) : (
        <></>
      ),
    },
    internship: {
      favouritePath: "internship_map.favourite",
      title: "Mapa staży podyplomowych",
      desc: "W tym miejscu znajdziesz mapę wszystkich jednostek akredytowanych do prowadzenia staży podyplomowych. Po wybraniu konkretnego szpitala dowiesz się, jakie staże możesz w nim realizować, które należy odbyć w placówkach zewnętrznych i jakie są opinie na temat danego miejsca.",
      filters: (
        <InternshipFilters
          filtersSource={filterSource as InternshipMapFilterSource}
        />
      ),
      facilityList: (
        <LazyList
          resetMap={resetMap}
          facilities={facilities}
          geoFilter={geoFilter}
          setupType={setupType}
          inFavourite={!filterSource}
          filterSource={filterSource}
        />
      ),
      facilityTypeFilter: <FacilityTypeFilter filters={internshipMapFilters} />,
      map: isClient ? (
        <InteractiveMap
          facilities={facilities}
          setGeoFilter={setGeoFilter}
          type={"internship"}
          resetState={resetState}
          resetMap={resetMap}
          // Hide medical chambers, because they are making chaos on the map
          // medicalChambers={medicalChambers}
        />
      ) : (
        <></>
      ),
    },
  };
  const { title, desc, filters, facilityTypeFilter, map, facilityList } =
    setups[setupType];

  return (
    <div className={"@container"}>
      {!favouriteOnly ? (
        <>
          <div
            id={"dashboard-content"}
            className={"mb-8 flex items-center gap-4"}
          >
            <SectionTitleWithIcon
              sectionKey={
                setupType === "residency" ? "residencyMap" : "internshipMap"
              }
            >
              <h1 className={"text-xl font-semibold text-zinc-800 md:text-2xl"}>
                {title}
              </h1>
            </SectionTitleWithIcon>
          </div>

          {filterSource === false ? (
            <></>
          ) : (
            <div className={"mb-3 bg-white py-3 xl:sticky xl:top-0 xl:z-10"}>
              {filters}
            </div>
          )}
        </>
      ) : (
        <SectionTitleWithIcon
          sectionKey={
            setupType === "residency" ? "residencyMap" : "internshipMap"
          }
        >
          <h1 className={"text-xl font-semibold text-zinc-800 md:text-2xl"}>
            Moja lista
          </h1>
        </SectionTitleWithIcon>
      )}

      <div
        className={cn(
          "@container z-10 bg-white xl:sticky",
          favouriteOnly ? "pt-2 xl:top-0" : "xl:top-16",
        )}
      >
        <div
          className={
            "@960:grid-cols-2 @960:items-center mb-4 grid grid-cols-1 gap-8"
          }
        >
          <div
            className={cn(
              "flex flex-col gap-4",
              "@512:flex-row @512:items-center @512:justify-between",
            )}
          >
            <div>
              <TabNav favouriteOnly={favouriteOnly} setupType={setupType} />{" "}
            </div>
            {filterSource === false ? (
              <></>
            ) : (
              <div
                className={"flex flex-col items-center justify-between gap-2"}
              >
                <Sort
                  sortOptions={doctorTrainingDashboardSorts}
                  defaultLabel={"Alfabetycznie A-Z"}
                />
              </div>
            )}
          </div>
          {favouriteOnly ? (
            <></>
          ) : (
            <div className={""}>{facilityTypeFilter}</div>
          )}
        </div>
      </div>

      <div className={"@container"}>
        <div
          className={cn(
            "flex flex-col-reverse",
            "@896:grid @896:grid-cols-2 @896:gap-6 @896:items-start",
          )}
        >
          <div className={"h-full"}>{facilityList}</div>
          <div
            className={cn(
              "z-0 mb-4 flex justify-center xl:sticky [&_.leaflet-tile-pane]:filter-[grayscale(1)]",
              favouriteOnly ? "xl:top-[3.5rem]" : "xl:top-[7rem]",
            )}
          >
            {map}
          </div>
        </div>
      </div>
    </div>
  );
};

const TabNav = ({
  favouriteOnly,
  setupType,
}: {
  favouriteOnly: boolean;
  setupType: DoctorTrainingType;
}) => {
  const router = useRouter();
  return (
    <Tabs value={favouriteOnly ? "favourite" : "list"}>
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger
          value={"list"}
          className={"hover:cursor-pointer"}
          onClick={() => {
            router.push(
              createPath(
                setupType === "residency"
                  ? "residency_map.index"
                  : "internship_map.index",
              ),
            );
          }}
        >
          Lista
        </TabsTrigger>
        <TabsTrigger
          value={"favourite"}
          className={"hover:cursor-pointer"}
          onClick={() => {
            router.push(
              createPath(
                setupType === "residency"
                  ? "residency_map.favourite"
                  : "internship_map.favourite",
              ),
            );
          }}
        >
          Zapisane
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
};
